import React from "react";
import Header from "../components/Header";
import { CheckCircle, ArrowRight, Repeat, LogOut } from "lucide-react";
import { filledPatentsFrom } from "../utils/patentUtils";

const styles = `:root{
  --primary: #0b63d8;
  --accent: #ff6b6b;
  --muted: #24303f;
  --bg: linear-gradient(180deg,#f8fbff,#f4f8ff);
  --card-bg: #ffffff;
}
.page-wrap{min-height:100vh; display:flex; flex-direction:column; background:var(--bg);}
.main{max-width:1100px; margin:3rem auto; padding:2rem; display:flex; gap:2rem; align-items:center; background: var(--card-bg); border-radius:16px; box-shadow: 0 12px 40px rgba(11,35,74,0.08);} 
.left{flex:1; padding:2rem 2.5rem;}
.badge{display:inline-flex; align-items:center; gap:12px; background: rgba(11,99,216,0.08); color:var(--primary); padding:12px 16px; border-radius:999px; font-weight:800;}
.h1{font-size:28px; margin:18px 0 8px; color:#082244; font-weight:900;}
.lead{color: #416ea4; font-size:15px; margin-bottom:18px;}
.stats-row{display:flex; gap:12px; margin-top:8px}
.stat-block{flex:1; padding:14px; border-radius:12px; background:linear-gradient(180deg, rgba(3,102,214,0.04), rgba(3,102,214,0.02));}
.stat-number{font-size:20px; font-weight:800}
.stat-label{font-size:12px; color:#1b2735}
.main .actions{display:flex; gap:16px; margin-top:26px; align-items:center}
.main .btn{padding:14px 20px; border-radius:12px; font-weight:700; cursor:pointer; border:none; display:inline-flex; align-items:center; gap:12px; min-height:48px; font-size:15px; min-width:150px; justify-content:center}
.main .btn-primary{background:linear-gradient(90deg,var(--primary), #1a4fb8); color:white; box-shadow:0 10px 28px rgba(11,63,150,0.10)}
.main .btn-outline{background:white; border:1px solid #e6eefc; color:#0b1736; padding:13px 18px}
.main .btn-ghost{background:transparent; color:#0b1736}
.main .btn-logout{background:#ef4444; border:1px solid #ef4444; color:#fff;}
.main .btn-logout:hover{background:#dc2626; box-shadow:0 8px 20px rgba(220,38,38,0.08);} 
.right{width:260px; display:flex; align-items:center; justify-content:center}
.hero-blob{width:200px;height:200px;border-radius:20px;background:linear-gradient(135deg, #e6f0ff, #dff3ff);display:flex;align-items:center;justify-content:center;box-shadow:0 10px 40px rgba(11,63,150,0.06);} 
.hero-icon{width:96px; height:96px; display:flex; align-items:center; justify-content:center}
.hero-blob .small{color:var(--muted); font-size:13px; margin-top:12px}

.hero-icon svg{ transform-origin: 50% 50%; }

/* stat color variants */
.stat-block.blue{ background: linear-gradient(180deg, rgba(14,91,191,0.04), rgba(14,91,191,0.02)); border-left: 4px solid #0b63d8 }
.stat-block.green{ background: linear-gradient(180deg, rgba(5,166,115,0.04), rgba(5,166,115,0.02)); border-left: 4px solid #10b981 }
.stat-block.purple{ background: linear-gradient(180deg, rgba(99,102,241,0.04), rgba(99,102,241,0.02)); border-left: 4px solid #6366f1 }

.stat-block.blue .stat-number{ color: #083a78 }
.stat-block.green .stat-number{ color: #04664f }
.stat-block.purple .stat-number{ color: #4f46e5 }

@media (max-width:900px){ .main{flex-direction:column;} .right{width:100%; order: -1} .actions{flex-direction:column; align-items:stretch} }
`;

const ThankYouPage = ({ backToDashboard, startNew, onLogout, data }) => {
  const filled = filledPatentsFrom(data || {});
  const patentsCount = filled.length || 0;
  const featuresCount = (data?.features || []).filter(
    (f) => f && f.text && f.text.trim()
  ).length;
  const keywordsCount = (data?.keywords || []).filter(
    (k) => k && k.text && k.text.trim()
  ).length;

  return (
    <div className="page-wrap">
      <style>{styles}</style>
      <Header active="download" onLogout={onLogout} />
      <main style={{ flex: 1 }}>
        <div className="main">
          <div className="left">
            <div className="badge">
              <CheckCircle size={20} /> Report Generated
            </div>

            <h2 className="h1">Your Prior Art Report is Ready</h2>
            <p className="lead">
              We've generated a polished, professional Prior Art Search report.
              You can return to the dashboard, start a fresh project, or sign
              out.
            </p>

            <div className="stats-row">
              <div className="stat-block blue">
                <div className="stat-number">{patentsCount}</div>
                <div className="stat-label">References</div>
              </div>
              <div className="stat-block green">
                <div className="stat-number">{featuresCount}</div>
                <div className="stat-label">Features</div>
              </div>
              <div className="stat-block purple">
                <div className="stat-number">{keywordsCount}</div>
                <div className="stat-label">Keywords</div>
              </div>
            </div>

            <div className="actions">
              <button
                className="btn btn-primary"
                onClick={() => {
                  if (typeof backToDashboard === "function") backToDashboard();
                }}
              >
                Back to Dashboard <ArrowRight size={16} />
              </button>

              <button
                className="btn btn-outline"
                onClick={() => {
                  if (typeof startNew === "function") startNew();
                }}
              >
                Start New Project <Repeat size={16} />
              </button>

              <button
                className="btn btn-logout"
                onClick={() => {
                  if (typeof onLogout === "function") onLogout();
                }}
              >
                <LogOut size={16} /> Logout
              </button>
            </div>
          </div>

          <aside className="right" aria-hidden>
            <div className="hero-blob">
              <div style={{ textAlign: "center" }}>
                <div
                  style={{
                    width: 96,
                    height: 96,
                    margin: "0 auto",
                    borderRadius: 18,
                    background: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 8px 30px rgba(11,63,150,0.06)",
                  }}
                >
                  <div className="hero-icon">
                    <CheckCircle size={48} color="#0b63d8" />
                  </div>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
};

export default ThankYouPage;
